import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  LineChart,
  Line,
} from 'recharts';

const COLORS = [
  '#3B82F6', // blue
  '#10B981', // green
  '#F59E0B', // amber
  '#EF4444', // red
  '#8B5CF6', // purple
  '#EC4899', // pink
  '#06B6D4', // cyan
  '#F97316', // orange
  '#84CC16', // lime
  '#6366F1', // indigo
];

interface ChartData {
  name: string;
  value: number;
  [key: string]: any;
}

interface ReportChartProps {
  type: 'pie' | 'bar' | 'line' | 'horizontal-bar';
  data: ChartData[];
  dataKey?: string;
  nameKey?: string;
  height?: number;
  showLegend?: boolean;
  showGrid?: boolean;
  title?: string;
}

export default function ReportChart({
  type,
  data,
  dataKey = 'value',
  nameKey = 'name',
  height = 300,
  showLegend = true,
  showGrid = true,
  title,
}: ReportChartProps) {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <p className="text-muted-foreground">No data available for chart</p>
      </div>
    );
  }

  const renderPieChart = () => (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          labelLine={false}
          outerRadius={80}
          fill="#8884d8"
          dataKey={dataKey}
          nameKey={nameKey}
          label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
        >
          {data.map((_, index) => (
            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
          ))}
        </Pie>
        <Tooltip 
          formatter={(value: number) => [value.toLocaleString(), 'Count']}
        />
        {showLegend && <Legend />}
      </PieChart>
    </ResponsiveContainer>
  );

  const renderBarChart = () => (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data}>
        {showGrid && <CartesianGrid strokeDasharray="3 3" />}
        <XAxis 
          dataKey={nameKey} 
          tick={{ fontSize: 12 }}
          interval={0}
          angle={-45}
          textAnchor="end"
          height={60}
        />
        <YAxis tick={{ fontSize: 12 }} />
        <Tooltip 
          formatter={(value: number) => [value.toLocaleString(), title || 'Value']}
        />
        {showLegend && <Legend />}
        <Bar dataKey={dataKey} fill="#3B82F6" radius={[4, 4, 0, 0]}>
          {data.map((_, index) => (
            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );

  const renderHorizontalBarChart = () => (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} layout="vertical">
        {showGrid && <CartesianGrid strokeDasharray="3 3" />}
        <XAxis type="number" tick={{ fontSize: 12 }} />
        <YAxis 
          dataKey={nameKey} 
          type="category" 
          tick={{ fontSize: 12 }}
          width={120}
        />
        <Tooltip 
          formatter={(value: number) => [value.toLocaleString(), title || 'Value']}
        />
        {showLegend && <Legend />}
        <Bar dataKey={dataKey} fill="#3B82F6" radius={[0, 4, 4, 0]}>
          {data.map((_, index) => (
            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );

  const renderLineChart = () => (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data}>
        {showGrid && <CartesianGrid strokeDasharray="3 3" />}
        <XAxis 
          dataKey={nameKey} 
          tick={{ fontSize: 12 }}
        />
        <YAxis tick={{ fontSize: 12 }} />
        <Tooltip 
          formatter={(value: number) => [value.toLocaleString(), title || 'Value']}
        />
        {showLegend && <Legend />}
        <Line 
          type="monotone" 
          dataKey={dataKey} 
          stroke="#3B82F6" 
          strokeWidth={2}
          dot={{ fill: '#3B82F6' }}
        />
      </LineChart>
    </ResponsiveContainer>
  );

  switch (type) {
    case 'pie':
      return renderPieChart();
    case 'bar':
      return renderBarChart();
    case 'horizontal-bar':
      return renderHorizontalBarChart();
    case 'line':
      return renderLineChart();
    default:
      return renderBarChart();
  }
}
